import { useCallback } from 'react'

import { useLocation, useNavigate } from 'react-router'

/**
 * A simple hash function to generate a consistent hash from a string.
 * This is not cryptographically secure but serves well for generating short identifiers.
 */
export const hashString = (str: string) => {
  let hash = 0.1 // start with non-zero float to avoid trivial zeros
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i) * 7) % 1_000_000_000
  }
  return Math.floor(hash).toString(36)
}

/**
 * Replace or add a hash segment (e.g. #bp123) while preserving others.
 */
export const useHashUpdater = (defaultPrefix: string) => {
  const location = useLocation()
  const navigate = useNavigate()

  return {
    /**
     * Update the hash with a new value for the given prefix. If the value is empty, it removes that segment.
     *
     * @param value The value to set for the prefix. If empty, the prefix segment will be removed.
     * @param prefix The prefix to identify the hash segment (default is 'bp').
     * @returns true if the hash was updated, false if the new value is the same as the current one.
     */
    updateHash: useCallback(
      (value: string, prefix: string = defaultPrefix) => {
        const currentHash = location.hash.replace(/^#/, '')
        const segments = currentHash ? currentHash.split('#') : []
        const hashValue = hashString(value)

        // Check if the current hash already has the desired value
        if (segments.includes(`${prefix}${hashValue}`)) return false

        // Remove any existing segment with the same prefix
        const filtered = segments.filter((s) => !s.startsWith(prefix))

        // Add the new one if value is non-empty
        if (value) filtered.push(`${prefix}${hashValue}`)

        // Construct new hash
        const newHash = filtered.length ? `#${filtered.join('#')}` : ''

        void navigate(`${location.pathname}${location.search}${newHash}`, { replace: true })
        return true
      },
      [location, navigate, defaultPrefix],
    ),
    /**
     * Find the hash value for a given prefix.
     *
     * @param prefix The prefix to search for in the hash.
     * @param hash The hash string to search within.
     * @returns The value associated with the prefix, or null if not found.
     */
    getHashValue: useCallback(() => {
      const segments = location.hash.replace(/^#/, '').split('#')
      const match = segments.find((s) => s.startsWith(defaultPrefix))
      return match ? match.slice(defaultPrefix.length) : null
    }, [location.hash, defaultPrefix]),
  }
}
